import React from "react";
import { connect } from "react-redux";
import {
  FieldArray,
  Field,
  reduxForm,
  change,
  getFormValues,
} from "redux-form";
import _ from "lodash";

import Input from "components/Input";
import Select from "components/Select";
import AddBlock from "../../partials/AddBlock";
import BackgroundBlock from "../../partials/BackgroundBlock";
import ButtonsRow from "../../partials/ButtonsRow";
import CommentBlock from "../../partials/Comment";
import Validations from "helpers/validations";
import { getFormatted, limitDecimals } from "helpers/formatting";
import PreferencesActions from "../../preferences/actions";

import "./style.scss";

interface IProps {
  formValues: any;
  invalid: boolean;
  changeField: (fieldname: string, value: any) => void;
  goNext: () => void;
  goBack: () => void;
  itemOptions: any[];
  getItemOptions: () => void;
  ownerTypes: any[];
  transferTypes: any[];
  settings: any;
}

class StepTwo extends React.Component<IProps> {
  public componentDidMount() {
    const { formValues } = this.props;

    formValues.fustLines?.forEach((__, i) =>
      this.calculateTotal(i, "", formValues)
    );

    this.props.getItemOptions();
  }

  private calculateTotal = (index, value, allValues) => {
    const fust = _.toNumber(_.get(allValues, `fustLines[${index}].fust`));
    const price = _.get(allValues, `fustLines[${index}].price`);

    const total = limitDecimals(fust * price);

    if (!_.isNaN(total)) {
      this.props.changeField(`fustLines[${index}].total`, total);
    }

    return value;
  };

  private addNewFustLine = () => {
    const { ownerTypes, transferTypes, settings, formValues, changeField } =
      this.props;
    const line = {
      type: _.find(ownerTypes, ["value", formValues?.kegStandardFromValue]),
      buyContainer: _.find(transferTypes, ["value", "NVT"]),
      costTo: _.get(this.getOrderNumbers(), "[0]", ""),
      comment: _.get(settings, "Settings.fustComment", ""),
    };
    const fustLines = [...formValues.fustLines, line];
    changeField("fustLines", fustLines);
  };

  private getOrderNumbers = () =>
    _(this.props.formValues.orderLines)
      .map("orderNummer")
      .uniqBy("label")
      .value();

  public render() {
    const { goNext, goBack, formValues } = this.props;
    let isInvalid = false;

    formValues.fustLines?.map(({ fust }) => {
      if (!fust) {
        isInvalid = true;
      }
    });

    return (
      <div id="step-two">
        <FieldArray
          name="fustLines"
          component={this.renderFustLines}
          rerenderOnEveryChange
        />
        <ButtonsRow
          goNext={goNext}
          goBack={goBack}
          showBackBtn
          disabled={isInvalid}
        />
      </div>
    );
  }

  private renderFustLines = ({ fields }) => (
    <div>
      {fields.map((item, index) => {
        const buyContainer = _.get(
          this.props.formValues,
          `fustLines[${index}].buyContainer.value`
        );

        return (
          <BackgroundBlock
            key={index}
            showCloseBtn
            removeBlock={() => fields.remove(index)}
          >
            <div className="step-two-row">
              <Field
                containerClassName="form-field"
                name={`${item}.fust`}
                label="DELIVERIES_FUST"
                component={Input}
                type="number"
                normalize={(value, __, formValues) =>
                  this.calculateTotal(index, value, formValues)
                }
                parse={(value) => value && parseInt(value, 10)}
                validate={[Validations.positive, Validations.required]}
              />
              <Field
                containerClassName="form-field"
                className="min-width-select-forms"
                name={`${item}.type`}
                label="TYPE"
                component={Select}
                options={this.props.ownerTypes}
                validate={buyContainer === "NVT" ? [] : [Validations.required]}
              />
              <Field
                containerClassName="form-field"
                className="min-width-select-forms"
                name={`${item}.kind`}
                label="KIND"
                component={Select}
                options={this.props.itemOptions}
                validate={buyContainer === "NVT" ? [] : [Validations.required]}
              />
              <Field
                containerClassName="form-field"
                className="min-width-select-forms"
                name={`${item}.buyContainer`}
                label="DELIVERIES_TRANSFER"
                component={Select}
                options={this.props.transferTypes}
                onChange={(e, option) =>
                  _.get(option, "value") !== "Yes" &&
                  this.props.changeField(`fustLines[${index}].price`, "")
                }
                validate={[Validations.required]}
              />
              <Field
                containerClassName="form-field"
                name={`${item}.price`}
                label="DELIVERIES_PRICE_PER_ONE"
                type="number"
                component={Input}
                normalize={(value, __, allValues) =>
                  this.calculateTotal(index, value, allValues)
                }
                parse={limitDecimals}
                disabled={buyContainer !== "Yes"}
                validate={[Validations.positive]}
              />
              <Field
                containerClassName="form-field"
                name={`${item}.total`}
                format={(value) => getFormatted(value, "number")}
                label="TOTAL"
                component={Input}
                disabled
              />
              <Field
                containerClassName="form-field"
                className="min-width-select-forms"
                name={`${item}.costTo`}
                label="DELIVERIES_FOR"
                component={Select}
                options={this.getOrderNumbers()}
                validate={buyContainer === "NVT" ? [] : [Validations.required]}
              />
            </div>
            <Field
              name={`${item}.comment`}
              component={CommentBlock}
              maxLength={70}
            />
          </BackgroundBlock>
        );
      })}
      <AddBlock addBlock={this.addNewFustLine} text="DELIVERIES_ADD_LINE" />
    </div>
  );
}

const StepThreeWithReduxForm = reduxForm({
  form: "delivery",
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(StepTwo);

export default connect(
  (state) => ({
    formValues: getFormValues("delivery")(state),
    itemOptions: state.preferences.itemOptions,
    ownerTypes: state.preferences.ownerTypes,
    transferTypes: state.preferences.transferTypes,
    settings: state.preferences.settings,
  }),
  {
    changeField: (fieldName, value) => change("delivery", fieldName, value),
    getItemOptions: PreferencesActions.getItemOptions,
  }
)(StepThreeWithReduxForm);
